import { ApiErrorBody, type ErrorCode } from '@radar/contracts';
import type { AppType } from '@radar/server/app';
import { type ClientResponse, hc } from 'hono/client';
import { isMessageKey, t } from '@/i18n/useT';

// Client Hono RPC (`frontend.md` § 6). `AppType` est importé en type seul : le bundle du SPA ne
// contient aucun code serveur (vérifié par `src/lib/server-imports.test.ts` et `scripts/check-bundle.mjs`).

export const API_BASE = '/api/v1';

/**
 * Origine absolue : identique à `'/'` dans le navigateur ; nécessaire hors navigateur (tests jsdom +
 * MSW), où le `fetch` de Node refuse les URL relatives.
 */
export function apiOrigin(): string {
  return typeof location === 'undefined' ? 'http://localhost' : location.origin;
}

/** URL absolue d'une route `/api/v1/<path>` pour les rares appels hors client typé. */
export function apiUrl(path: string, query?: Record<string, string | undefined>): string {
  const url = new URL(`${API_BASE}${path}`, apiOrigin());
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, value);
  }
  return url.toString();
}

export const client = hc<AppType>(apiOrigin(), { init: { credentials: 'include' } });

/** Racine typée de l'API : `api.me.$get()`, `api.seeds.$get({ query })`, `api.seeds[':id'].$patch(...)`. */
export const api = client.api.v1;

/** `ErrorCode` du serveur, plus `network` quand la requête n'a pas abouti (hors ligne, DNS…). */
export type ApiErrorCode = ErrorCode | 'network';

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.details = details;
    this.requestId = requestId;
  }
}

/** Réponse JSON typée du client `hc` (tout statut). */
export type JsonResponse<T> = ClientResponse<T, number, 'json'>;

/**
 * Attend une réponse du client typé et renvoie son corps JSON, typé depuis la route :
 * `const me = await call(api.me.$get())`. Une réponse `204` renvoie `undefined`.
 * Erreur du serveur (`{ error: { code, … } }`) → `ApiError` avec `code` typé ; réponse non JSON →
 * `internal_error` avec le statut HTTP ; échec réseau → `network` (statut 0).
 */
export function call<T>(request: Promise<JsonResponse<T>>): Promise<T>;
/** Variante pour un `fetch` brut (route pas encore typée) : `call<Shape>(fetch(apiUrl('/x')))`. */
export function call<T = void>(request: Promise<Response>): Promise<T>;
export async function call<T>(request: Promise<JsonResponse<T> | Response>): Promise<T> {
  let res: JsonResponse<T> | Response;
  try {
    res = await request;
  } catch (cause) {
    throw new ApiError(0, 'network', t('errors.network'), cause);
  }
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  const body: unknown = await res.json().catch(() => null);
  const parsed = ApiErrorBody.safeParse(body);
  if (parsed.success) {
    const { code, message, details, requestId } = parsed.data.error;
    throw new ApiError(res.status, code, message, details, requestId);
  }
  throw new ApiError(res.status, 'internal_error', `HTTP ${res.status}`, body ?? undefined);
}

/** `details.reason` d'une erreur (`seed_in_use`, `label_exists`…), s'il existe. */
export function apiErrorReason(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const details = error.details;
  if (typeof details !== 'object' || details === null || !('reason' in details)) return undefined;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' ? reason : undefined;
}

/**
 * Message utilisateur d'une erreur : `errors.<reason>` si `details.reason` a une traduction,
 * sinon `errors.<code>` si connu, sinon `errors.unknown`.
 */
export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const reasonKey = `errors.${apiErrorReason(error) ?? ''}`;
    if (isMessageKey(reasonKey)) return t(reasonKey);
    const key = `errors.${error.code}`;
    if (isMessageKey(key)) return t(key);
  }
  return t('errors.unknown');
}
